import React, { useCallback, useRef } from 'react';
import { Paperclip, Loader2, X, AlertCircle } from 'lucide-react';

/**
 * AttachmentBar — pick files, show upload progress, surface failures BEFORE send.
 *
 * #623 is explicit that a silent failure at send is the worst outcome, so an
 * attachment that failed to upload is shown as failed here, in the composer,
 * and the caller blocks Send while anything is still in flight.
 *
 * Deliberately NOT built on `RichTextEditor`'s `onImageUpload` prop. That prop
 * exists (no caller anywhere passes one) and inserts an inline `<img>` into the
 * body — a different feature from attaching a file. Conflating them would put
 * the image in the message body and nothing in the attachment list.
 *
 * The size cap is a CONVENIENCE here, never the authorisation: the server
 * enforces it independently, exactly as it does for `from_address`. The cap is
 * surfaced in the UI because Microsoft Graph carries an inline `fileAttachment`
 * only under ~3 MB — above that it needs a draft plus an upload session, a
 * different flow that this release does not implement.
 */

export const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;      // Graph inline ceiling
export const MAX_ATTACHMENTS_TOTAL_BYTES = 10 * 1024 * 1024;

export function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why a file is refused, or null when it is acceptable.
 * Exported so the caller and the tests share one rule.
 */
export function attachmentRejectionReason(file, existing = []) {
    if (file.size > MAX_ATTACHMENT_BYTES) {
        return `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_ATTACHMENT_BYTES)} per file.`;
    }
    const total = existing.reduce((sum, a) => sum + (a.size || 0), 0) + file.size;
    if (total > MAX_ATTACHMENTS_TOTAL_BYTES) {
        return `Adding ${file.name} would exceed the ${formatBytes(MAX_ATTACHMENTS_TOTAL_BYTES)} total attachment limit.`;
    }
    return null;
}

export default function AttachmentBar({
    attachments = [],
    onAdd,
    onRemove,
    disabled = false,
    idPrefix = 'composer',
}) {
    const inputRef = useRef(null);

    const handleFiles = useCallback((event) => {
        const files = Array.from(event.target.files || []);
        if (files.length) onAdd?.(files);
        // Reset so picking the SAME file twice still fires a change event.
        event.target.value = '';
    }, [onAdd]);

    return (
        <div className="space-y-1" data-testid={`attachment-bar-${idPrefix}`}>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={disabled}
                    className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 disabled:opacity-50"
                    data-testid={`attach-button-${idPrefix}`}
                >
                    <Paperclip className="w-3.5 h-3.5" />
                    Attach
                </button>
                <span className="text-[10px] text-stone-400">
                    Up to {formatBytes(MAX_ATTACHMENT_BYTES)} per file
                </span>
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFiles}
                    data-testid={`attach-input-${idPrefix}`}
                />
            </div>

            {attachments.length > 0 && (
                <ul className="flex flex-wrap gap-1.5" data-testid={`attachment-list-${idPrefix}`}>
                    {attachments.map(item => (
                        <li
                            key={item.id}
                            className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs ${
                                item.status === 'error'
                                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                                    : 'border-stone-200 bg-white text-stone-600'
                            }`}
                            data-testid={`attachment-${item.id}`}
                        >
                            {item.status === 'uploading' && <Loader2 className="w-3 h-3 animate-spin" />}
                            {item.status === 'error' && <AlertCircle className="w-3 h-3" />}
                            <span className="max-w-[16rem] truncate">{item.name}</span>
                            <span className="text-[10px] text-stone-400">{formatBytes(item.size)}</span>
                            {item.status === 'uploading' && typeof item.progress === 'number' && (
                                <span className="text-[10px] text-stone-400">{item.progress}%</span>
                            )}
                            {/* The failure is stated here, before Send — never
                                discovered after the user believes it was sent. */}
                            {item.status === 'error' && (
                                <span className="text-[10px]">{item.error || 'Upload failed'}</span>
                            )}
                            <button
                                type="button"
                                onClick={() => onRemove?.(item.id)}
                                aria-label={`Remove ${item.name}`}
                                className="text-stone-400 hover:text-stone-700"
                                data-testid={`attachment-remove-${item.id}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
